import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ReturnTarget } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { refusalText } from '../lib/reach';
import { FormPage } from '../components/FormPage';
import { Field, Select, Textarea, useToast } from '../components/ui';

/**
 * Sending a workflow's step back to someone earlier to check something, and its
 * way back (operator, 2026-10-15: "everyone on this workflow can return task to
 * previews employee to check for something when this employee finish it will
 * return to the person who ask me to do something not same workflow"). Both are
 * pages, as every form in the admin is.
 */

const REFRESH = [['tasks'], ['kanban'], ['dashboard'], ['leader-tasks']];

/**
 * Route: `/tasks/:id/return` — the step's own person, while at it, sends it to
 * someone who did the work on an earlier level, saying what to check. It stays
 * where it is in the workflow, and comes back to them when that person is done.
 */
export function TaskReturnPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [toUserId, setTo] = useState('');
  const [note, setNote] = useState('');

  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const targets: ReturnTarget[] = task.data?.returnTargets ?? [];
  const allowed = !!task.data?.can?.returnStep;
  const target = targets.find((p) => p.id === toUserId) ?? null;

  const send = useMutation({
    mutationFn: () => api.post(`/tasks/${id}/return`, { toUserId, note: note.trim() }),
    onSuccess: () => {
      for (const queryKey of [['task', id], ...REFRESH]) void queryClient.invalidateQueries({ queryKey });
      toast.success(t('tasks.return.sent', { name: target ? localized(target) : '' }));
      // Still theirs to open: it comes back to them.
      navigate(`/tasks/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  return (
    <FormPage
      title={t('tasks.return.title')}
      subtitle={task.data?.title}
      backTo={`/tasks/${id}`}
      backLabel={task.data?.title ?? t('nav.tasks')}
      submitLabel={t('tasks.return.submit')}
      submitting={send.isPending}
      disabled={!allowed || !toUserId || !note.trim()}
      loading={task.isLoading}
      error={task.error}
      onRetry={() => void task.refetch()}
      onSubmit={() => send.mutate()}
    >
      <div className="space-y-4">
        {task.data && !allowed && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('tasks.return.notYours')}
          </p>
        )}
        <Field label={t('tasks.return.to')} required hint={t('tasks.return.toHint')}>
          <Select value={toUserId} onChange={(e) => setTo(e.target.value)} disabled={!allowed} autoFocus>
            <option value="">—</option>
            {targets.map((p) => (
              <option key={p.id} value={p.id}>
                {t('tasks.return.option', { name: localized(p), n: formatNumber(p.level, locale) })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('tasks.return.note')} required hint={t('tasks.return.noteHint')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!allowed} dir="auto" />
        </Field>
      </div>
    </FormPage>
  );
}

/**
 * Route: `/tasks/:id/return-back` — a step out for a check goes back to its own
 * person: whoever is checking it, when done, with a word for them if they like;
 * the person who asked, to take it back sooner; a team leader or an admin,
 * should the one checking be away. Straight back to them — not on through the
 * levels.
 */
export function TaskReturnBackPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const data = task.data;
  const out = !!data?.returnToUserId;
  const allowed = out && !!data?.can?.endCheck;
  const owner = data?.returnTo ? localized(data.returnTo) : '';
  const checker = data?.assignee ? localized(data.assignee) : '';
  const checking = !!data && data.assigneeUserId === user?.id;
  const asked = !!data && data.returnToUserId === user?.id;

  const back = useMutation({
    mutationFn: () => api.post(`/tasks/${id}/return-back`, { note: note.trim() || undefined }),
    onSuccess: () => {
      for (const queryKey of REFRESH) void queryClient.invalidateQueries({ queryKey });
      toast.success(t('tasks.return.back', { name: owner }));
      // Whoever checked it hands it back and, a step being private, can no longer open it — unless they lead.
      const opensStill = !checking || can(P.TASKS_ASSIGN) || can(P.TASKS_MANAGE_ALL);
      if (opensStill) {
        void queryClient.invalidateQueries({ queryKey: ['task', id] });
        navigate(`/tasks/${id}`, { replace: true });
      } else {
        queryClient.removeQueries({ queryKey: ['task', id] });
        navigate('/tasks', { replace: true });
      }
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  return (
    <FormPage
      title={asked ? t('tasks.return.takeBackTitle', { name: checker }) : t('tasks.return.backTitle', { name: owner })}
      subtitle={data?.title}
      backTo={`/tasks/${id}`}
      backLabel={data?.title ?? t('nav.tasks')}
      submitLabel={
        checking ? t('tasks.return.done', { name: owner }) : asked ? t('tasks.return.takeBack') : t('tasks.return.bringBack', { name: owner })
      }
      submitting={back.isPending}
      disabled={!allowed}
      loading={task.isLoading}
      error={task.error}
      onRetry={() => void task.refetch()}
      onSubmit={() => back.mutate()}
      summary={
        out &&
        data.returnNote && (
          <div>
            <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">
              {t('tasks.return.asked', { name: owner })}
            </span>
            <p className="mt-1 whitespace-pre-wrap break-words text-body text-[var(--text-primary)]" dir="auto">
              {data.returnNote}
            </p>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {data && !allowed && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {out ? t('tasks.return.notYoursToEnd') : t('tasks.return.notOut')}
          </p>
        )}
        <Field label={t('tasks.return.backNote', { name: owner })} hint={t('tasks.return.backNoteHint')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!allowed} dir="auto" autoFocus />
        </Field>
      </div>
    </FormPage>
  );
}
